import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { apiClient } from "../utils/apiClient";

interface CommonCsvExportProps {
  pageTitle: string; // ページヘッダーのタイトル (例: "業務報告出力")
  cardTitle: string; // カード内のタイトル (例: "期間指定ダウンロード")
  description: React.ReactNode; // 説明文
  apiPath: string; // APIのエンドポイント (例: "/api/work_log_exports")
  fileNamePrefix: string; // 保存時のファイル名プレフィックス (例: "work_logs")
  maxDate?: string | null; // 選択可能な最大日付 (YYYY-MM-DD)
}

const CommonCsvExport = ({
  pageTitle,
  cardTitle,
  description,
  apiPath,
  fileNamePrefix,
  maxDate,
}: CommonCsvExportProps) => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = formatDate(today);

  // maxDateが設定されており、かつ今日がmaxDateより未来の場合は、初期値をmaxDateに制限する
  const initialEndDate = maxDate && maxDate < todayStr ? maxDate : todayStr;

  const [startDate, setStartDate] = useState(formatDate(firstDay));
  const [endDate, setEndDate] = useState(initialEndDate);
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      alert("期間を指定してください");
      return;
    }

    setLoading(true);
    try {
      const blob = await apiClient.download(apiPath, {
        start_date: startDate,
        end_date: endDate,
      });

      // ファイル保存処理
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // ファイル名はここで生成
      a.download = `${fileNamePrefix}_${startDate}_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // エラーメッセージを表示
      alert(
        "ダウンロードに失敗しました: " +
          (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#f4f6f9" }}
    >
      <Header title={pageTitle} />

      <div className="container mt-5" style={{ maxWidth: "600px" }}>
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-decoration-none text-secondary d-inline-flex align-items-center"
          >
            <span className="fs-4 me-2">‹</span>
            管理者メニューに戻る
          </Link>
        </div>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 pt-4 px-4">
            <h5 className="fw-bold text-dark m-0">
              <span className="me-2">📊</span>
              {cardTitle}
            </h5>
            <div className="text-muted small mt-1">{description}</div>
          </div>

          <div className="card-body p-4">
            <div className="mb-4">
              <label className="form-label fw-bold small">対象期間</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  max={maxDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span className="input-group-text bg-light">～</span>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  max={maxDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <button
              className="btn btn-primary w-100 py-2 fw-bold shadow-sm"
              onClick={handleDownload}
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" />
                  生成中...
                </span>
              ) : (
                <span>📥 CSVをダウンロード</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonCsvExport;
